import {RESPONSE_TYPES, VRI_LEVEL} from "../constants";
import {ServerResponse} from "../models/models";

export const getFormattedMessage = (message, param1, param2, param3, param4) => {
    let newMessage = message;
    [param1, param2, param3, param4].forEach((param, idx) => {
        if (newMessage.includes(`{${idx}}`) && param !== undefined) {
            newMessage = newMessage.replace(`{${idx}}`, param);
        }
    });
    return newMessage;
}

export const getPrice = (prices, priceId) => {
    if (!prices) {
        console.error("getPrice: An invalid 'prices' was supplied");
        return null;
    } else if (!priceId) {
        console.error("getPrice: An invalid 'priceId' was supplied");
        return null;
    }

    for (let i = 0; i < prices.length; i++) {
        if (prices[i].id === priceId) {
            return prices[i];
        }
    }

    return null;
};

export const getUrlParam = (value) => {
    let searchParams = new URLSearchParams(window.location.search);
    if (!searchParams.has(value)) {
        return -1;
    }
    return searchParams.get(value);
};

export const daysBetween = (start, end) => {
    var diff = (end.getTime() - start.getTime());
    return Math.floor(diff / (1000 * 60 * 60 * 24));
};

export const getFutureDate = (oldDate, daysToAdd) => {
    const d = new Date(oldDate);        // clone
    d.setDate(d.getDate() + daysToAdd);
    return d;
};

export function hasCardExpired(cardDate) {
    return cardDate.getTime() < new Date().getTime();
}

export function isCardAboutToExpire(cardDate) {
    return cardDate.getTime() < getFutureDate(new Date(), 30).getTime();
}

export function isCardGoingToExpireBeforeDate(cardDate, expDate) {
    return cardDate.getTime() < expDate.getTime();
}

export const getDefaultPaymentMethod = (allPaymentMethods: Array) => {
    if (!allPaymentMethods) {
        return {};
    }

    for (let i = 0; i < allPaymentMethods.length; i++) {
        if (allPaymentMethods[i].isDefault) {
            return allPaymentMethods[i];
        }
    }

    if (allPaymentMethods.length >= 1) {
        return allPaymentMethods[0];
    }

    return {};
}

export const getPreferredACPaymentMethod = (allPaymentMethods: Array) =>
    getPreferredPaymentMethod(allPaymentMethods, 'preferredForAC');

export const getPreferredVRIPaymentMethod = (allPaymentMethods: Array) =>
    getPreferredPaymentMethod(allPaymentMethods, 'preferredForVRI');

export const getPreferredVDSPaymentMethod = (allPaymentMethods: Array) =>
    getPreferredPaymentMethod(allPaymentMethods, 'preferredForVDS');

const getPreferredPaymentMethod = (allPaymentMethods: Array, key: String) => {
    if (!allPaymentMethods) {
        return null;
    }

    let defaultMethod = null;

    for (let i = 0; i < allPaymentMethods.length; i++) {
        if (allPaymentMethods[i][key])
            return allPaymentMethods[i];

        if (allPaymentMethods[i].isDefault)
            defaultMethod = allPaymentMethods[i];
    }

    if (defaultMethod !== null)
        return defaultMethod;

    if (allPaymentMethods.length >= 1) {
        return allPaymentMethods[0];
    }

    return null;
}


export const getDateStr = (date) => {
    return new Date(date).toDateString();
};

export const isPlanActive = (date) => {
    if (!date) {
        return false;
    }
    return date >= new Date(new Date().toDateString());
}

export const convertPriceUnitAmountToReadable = (amount) => {
    return amount ? (amount / 100).toFixed(2) : null;
}

export const getAmountLabel = (priceAmount, months) => {
    const amount = convertPriceUnitAmountToReadable(priceAmount);
    if (amount === null) {
        return 'Free';
    } else if (months === 1) {
        return `$${amount}/month`;
    }
    return `$${amount}/year`;
};

export const openTermDetailsPage = (type) => {
    window.open(`TermDetails.act?type=${type}`,'popup',
        'menubar=no, status=no, width=500, height=700, top=400, left=700')?.focus();
};

export const loadResponsePage = (status, setLoading, setServerResponse) => {
    let title;
    let message;
    let type;
    switch (status) {
        case 'succeeded':
            title = i18n.payments.success;
            message = i18n.payments.successAction;
            type = RESPONSE_TYPES.success;
            break;
        case 'processing':
            title = i18n.payments.processing;
            message = i18n.payments.processingDetails;
            type = RESPONSE_TYPES.processing;
            break;
        case 'requires_payment_method':
            title = i18n.payments.error.genericTitle;
            message = i18n.payments.error.failedToProcessPayment;
            type = RESPONSE_TYPES.error;
            break;
    }

    setLoading(false);
    setServerResponse(new ServerResponse(type, title, message));
};

export const handleError = (result, msg, setServerResponse) => {
    console.error('handleError: ', result);
    let errorTitle = i18n.payments.error.genericTitle;
    if (result.statusText) {
      errorTitle = result.statusText;
    } else if (typeof result === 'string') {
        errorTitle = result;
    }

    let errorMsg = msg;
    if (result.data && typeof result.data === 'string') {
        errorMsg = result.data;
    }
    setServerResponse(new ServerResponse(RESPONSE_TYPES.error, errorTitle, errorMsg));
};

export const getSameVRITierPlanAtDifferentRecurrence = (levels, selectedPlan) => {
    if (!levels || !selectedPlan) {
        return null;
    }

    for (let i = 0; i < levels.length; i++) {
        const { maxInspectorCount, monthly, yearly } = levels[i];
        if (maxInspectorCount === selectedPlan.maxInspectorCount) {
            const paymentFormatData = selectedPlan.months === 1 ? yearly : monthly;
            return { ...selectedPlan, ...paymentFormatData };
        }
    }
    return null;
};

export const getSameVDSTierPlanAtDifferentRecurrence = (levels, selectedPlan) => {
    if (!levels || !selectedPlan) {
        return null;
    }

    for (let i = 0; i < levels.length; i++) {
        const { maxScanCount, monthly, yearly } = levels[i];
        if (maxScanCount === selectedPlan.maxScanCount) {
            const paymentFormatData = selectedPlan.months === 1 ? yearly : monthly;
            return { ...selectedPlan, ...paymentFormatData };
        }
    }
    return null;
};

export const setSelectedPlanAsOriginal = (levels, level, setSelectedPlan) => {
    if (!levels || !level || !setSelectedPlan) {
        return;
    }

    for (let i = 0; i < levels.length; i++) {
        const { monthly, yearly } = levels[i];
        if (monthly.value === level) {
            setSelectedPlan({...levels[i], ...monthly});
            break;
        } else if (yearly.value === level) {
            setSelectedPlan({...levels[i], ...yearly});
            break;
        }
    }
}

export const findLevel = (levels, value) => {
    if (!levels)
        return null;

    for (let i = 0; i < levels.length; i++) {
        const { monthly, yearly } = levels[i];
        if (monthly.value === value) {
            return {...levels[i], ...monthly};
        } else if (yearly.value === value) {
            return {...levels[i], ...yearly};
        }
    }
}

export const getDailyRateOfPlan = (priceAmount, isMonthly) => {
    let dailyRate = priceAmount;
    if (isMonthly) {
        dailyRate /= 30;
    } else {
        dailyRate /= 365;
    }
    return Math.floor((dailyRate / 100) * 100) / 100;
};


// Subscriptions

export const getSubmitBtnText = (isEligibleForFreeTrial, payNow, doNotRenew) => {
    if (isEligibleForFreeTrial) {
        return i18n.payments.beginFreeTrial;
    } else if (!payNow) {
        return i18n.payments.attachPaymentMethod;
    } else if (doNotRenew) {
        return i18n.payments.oneTimePurchaseNow;
    }
    return i18n.payments.purchaseSubscription;
}

export function getMinMaxCount(levels, key) {
    const scanCounts = levels.map(l => l[key]);
    return {
        min: Math.min(...scanCounts),
        max: Math.max(...scanCounts)
    };
}

export const getDetailsText = (doesNotExpire, daysUntilExpired, hasPaymentMethod, showFreeTrial,
                               doNotRenew, hasActiveFreeTrial, freeTrialExpirationDate) => {
    if (doesNotExpire) {
        return i18n.payments.sub.doesNotExpire;
    } else if (showFreeTrial) {
        return i18n.payments.sub.canBeginPlan;
    } else if (hasActiveFreeTrial && freeTrialExpirationDate) {
        const freeTrialDate = new Date(freeTrialExpirationDate);
        const daysUntilFreeTrialExpires = daysBetween(new Date(), freeTrialDate);
        let infoText = getFormattedMessage(i18n.payments.sub.daysUntilFreeTrialExpires, daysUntilFreeTrialExpires + 1, freeTrialDate.toDateString());
        if (doNotRenew) {
            infoText += ` ${i18n.payments.sub.setToNotRenew}`;
        } else if (hasPaymentMethod) {
            infoText += ` ${i18n.payments.sub.scheduledToRenew}`;
        } else {
            infoText += ` ${i18n.payments.sub.pleaseAttachPaymentMethod}`;
        }
        return infoText;
    } else if (daysUntilExpired > 0) {
        let infoText = getFormattedMessage(i18n.payments.sub.daysUntilExpired, daysUntilExpired + 1);
        if (doNotRenew) {
            infoText += ` ${i18n.payments.sub.setToNotRenew}`;
        } else if (hasPaymentMethod) {
            infoText += ` ${i18n.payments.sub.scheduledToRenew}`;
        } else {
            infoText += ` ${i18n.payments.sub.pleaseAttachPaymentMethod}`;
        }
        return infoText;
    } else if (daysUntilExpired === 0) {
        let infoText = i18n.payments.sub.expiresToday;
        if (doNotRenew) {
            infoText += ` ${i18n.payments.sub.setToNowRenew}`;
        } else if (hasPaymentMethod) {
            infoText += ` ${i18n.payments.sub.scheduledToRenewToday}`;
        } else {
            infoText += ` ${i18n.payments.sub.pleaseAttachPaymentMethodExpiresToday}`;
        }
        return infoText;
    } if (hasPaymentMethod) {
        return i18n.payments.sub.hasExpiredWithPaymentMethod;
    }

    return i18n.payments.sub.hasExpiredWithoutPaymentMethod;
}